import React, { useState, useRef, useEffect } from 'react';

export type BasicStatus = 'upcoming' | 'live' | 'completed';

export interface StatusFilterDropdownProps {
  /** Selected statuses. When all three are selected, this is effectively "All". */
  selected: BasicStatus[];
  onChange: (next: BasicStatus[]) => void;
  counts?: { upcoming: number; live: number; completed: number };
  className?: string;
}

const ALL_STATUSES: BasicStatus[] = ['upcoming', 'live', 'completed'];

const StatusFilterDropdown: React.FC<StatusFilterDropdownProps> = ({
  selected,
  onChange,
  counts,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const allSelected = ALL_STATUSES.every((s) => selected.includes(s));

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const toggleStatus = (status: BasicStatus) => {
    let next: BasicStatus[];
    if (selected.includes(status)) {
      next = selected.filter((s) => s !== status);
    } else {
      next = [...selected, status];
    }
    // If nothing selected, revert to all
    if (next.length === 0) {
      next = [...ALL_STATUSES];
    }
    onChange(next);
  };

  const handleAllToggle = () => {
    if (allSelected) {
      // If already all, keep all selected (avoids "none" state)
      return;
    }
    onChange([...ALL_STATUSES]);
  };

  const label = allSelected
    ? `All${counts ? ` (${counts.upcoming + counts.live + counts.completed})` : ''}`
    : [
        selected.includes('upcoming') ? `Upcoming${counts ? ` (${counts.upcoming})` : ''}` : null,
        selected.includes('live') ? `Live${counts ? ` (${counts.live})` : ''}` : null,
        selected.includes('completed') ? `Completed${counts ? ` (${counts.completed})` : ''}` : null,
      ]
        .filter(Boolean)
        .join(', ');

  const isChecked = (status: BasicStatus) => selected.includes(status);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="inline-flex items-center px-2.5 py-1.5 rounded-full border border-gray-200 bg-white text-xs sm:text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <span className="mr-1 text-gray-500">Status:</span>
        <span className="font-medium text-gray-900 truncate max-w-[8rem] sm:max-w-[12rem]">
          {label || 'All'}
        </span>
        <svg
          className="w-3 h-3 ml-1 text-gray-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 mt-1 w-52 rounded-md shadow-lg bg-white border border-gray-200 z-50">
          <div className="py-1 text-xs sm:text-sm">
            <button
              type="button"
              onClick={handleAllToggle}
              className="w-full flex items-center px-3 py-1.5 hover:bg-gray-50"
            >
              <input
                type="checkbox"
                readOnly
                checked={allSelected}
                className="h-3 w-3 mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="flex-1">
                All
                {counts ? ` (${counts.upcoming + counts.live + counts.completed})` : ''}
              </span>
            </button>
            <hr className="my-1 border-gray-100" />
            <button
              type="button"
              onClick={() => toggleStatus('upcoming')}
              className="w-full flex items-center px-3 py-1.5 hover:bg-gray-50"
            >
              <input
                type="checkbox"
                readOnly
                checked={isChecked('upcoming')}
                className="h-3 w-3 mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="flex-1">
                Upcoming{counts ? ` (${counts.upcoming})` : ''}
              </span>
            </button>
            <button
              type="button"
              onClick={() => toggleStatus('live')}
              className="w-full flex items-center px-3 py-1.5 hover:bg-gray-50"
            >
              <input
                type="checkbox"
                readOnly
                checked={isChecked('live')}
                className="h-3 w-3 mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="flex-1">
                Live{counts ? ` (${counts.live})` : ''}
              </span>
            </button>
            <button
              type="button"
              onClick={() => toggleStatus('completed')}
              className="w-full flex items-center px-3 py-1.5 hover:bg-gray-50"
            >
              <input
                type="checkbox"
                readOnly
                checked={isChecked('completed')}
                className="h-3 w-3 mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="flex-1">
                Completed{counts ? ` (${counts.completed})` : ''}
              </span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatusFilterDropdown;

